// §11.4 — the Browser's model: what a tab is, what the address bar means, and how history works.
//
// Pure, for the same reason the terminal's session is: "search or navigate" is a decision with a
// dozen edge cases (a bare hostname, a path, a query with a dot in it, an IPv6 literal) and each
// one is an assertion rather than a thing to click through.
//
// **No content proxy exists in this codebase** (D-015). That constrains the model: a frame either
// loads or it does not, and the Browser's job on a refusal is to say so and offer the real thing —
// never to fetch the page itself and strip the header.
import { COPY } from '@/copy';

/** §11.4 — the internal pages. `hangar://` is ours and resolves to no network at all. */
export const INTERNAL_SCHEME = 'hangar://';

export const INTERNAL_PAGES = ['home', 'history', 'bookmarks', 'about'] as const;
export type InternalPage = (typeof INTERNAL_PAGES)[number];

export const HOME_URL = `${INTERNAL_SCHEME}home`;

/** Where a bare word goes when it is not a URL (§11.4's "search-or-navigate"). */
export const SEARCH_URL = 'https://duckduckgo.com/?q=';

export type TabStatus =
  | 'idle'
  /** The frame is loading and has not said anything yet. */
  | 'loading'
  | 'loaded'
  /** §11.4 — the site refuses to be embedded; the interstitial is showing. */
  | 'refused';

export interface BrowserTab {
  readonly id: string;
  readonly url: string;
  readonly title: string;
  readonly status: TabStatus;
  /** Newest last; `index` points into it. Back and forward walk this. */
  readonly history: readonly string[];
  readonly index: number;
}

export interface HistoryEntry {
  readonly url: string;
  readonly title: string;
  readonly at: number;
}

export interface Bookmark {
  readonly url: string;
  readonly title: string;
}

/**
 * §11.4's address bar: navigate, or search.
 *
 * The rule is the one every browser converged on, and the order matters. An explicit scheme is
 * always a navigation. Something with a dot and no spaces is a hostname. Everything else is a
 * search — including `localhost` and `127.0.0.1`, which have no dot and would otherwise be
 * searched for, so they are named.
 */
export function resolveAddress(input: string): string {
  const trimmed = input.trim();
  if (trimmed === '') return HOME_URL;

  if (trimmed.startsWith(INTERNAL_SCHEME)) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;

  // Host-shaped is decided **before** the unknown-scheme rule, because `localhost:5173` is
  // scheme-shaped too and a browser that searched for it would be useless in development. The
  // difference is what follows the colon: a port is digits, a scheme's body is not.
  const looksLikeHost =
    !/\s/.test(trimmed) &&
    (/^localhost(:\d+)?([/?#]|$)/i.test(trimmed) ||
      /^\d{1,3}(\.\d{1,3}){3}(:\d+)?([/?#]|$)/.test(trimmed) ||
      /^[^/\s:]+\.[^/\s:]{2,}(:\d+)?([/?#]|$)/.test(trimmed));
  if (looksLikeHost) return `https://${trimmed}`;

  // A scheme we do not speak is a search, not a navigation — `javascript:` and `data:` must never
  // reach a frame's `src`, and there is no third scheme worth special-casing (§19.6).
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return SEARCH_URL + encodeURIComponent(trimmed);

  return SEARCH_URL + encodeURIComponent(trimmed);
}

/** The internal page a `hangar://` URL names, or `null` for one nobody defined. */
export function internalPageOf(url: string): InternalPage | null {
  if (!url.startsWith(INTERNAL_SCHEME)) return null;
  const name = url.slice(INTERNAL_SCHEME.length).replace(/\/$/, '');
  return (INTERNAL_PAGES as readonly string[]).includes(name) ? (name as InternalPage) : null;
}

/** What the tab strip shows. A URL is not a title, but it is better than nothing. */
export function titleFor(url: string, known?: string): string {
  if (known && known.trim() !== '') return known;
  const page = internalPageOf(url);
  if (page) return COPY.browser.pageTitle[page] ?? page;
  try {
    return new URL(url).hostname || url;
  } catch {
    return url;
  }
}

/**
 * §11.4 — favicons.
 *
 * Derived from the origin rather than fetched and cached: a `<img src>` the browser loads is one
 * request the *frame's* origin sees, which it would see anyway, and nothing about it passes
 * through our server. There is no proxy here and there is not going to be one (D-015).
 */
export function faviconFor(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    return `${parsed.origin}/favicon.ico`;
  } catch {
    return null;
  }
}

let nextTabId = 0;

export function newTab(url = HOME_URL): BrowserTab {
  nextTabId += 1;
  return {
    id: `b${nextTabId}`,
    url,
    title: titleFor(url),
    status: internalPageOf(url) ? 'loaded' : 'loading',
    history: [url],
    index: 0,
  };
}

/**
 * Navigate a tab. Anything ahead of the current position is discarded, which is what makes
 * forward mean "somewhere I have actually been".
 */
export function navigate(tab: BrowserTab, url: string): BrowserTab {
  if (url === tab.url) return { ...tab, status: internalPageOf(url) ? 'loaded' : 'loading' };
  const history = [...tab.history.slice(0, tab.index + 1), url];
  return {
    ...tab,
    url,
    title: titleFor(url),
    status: internalPageOf(url) ? 'loaded' : 'loading',
    history,
    index: history.length - 1,
  };
}

export const canGoBack = (tab: BrowserTab): boolean => tab.index > 0;
export const canGoForward = (tab: BrowserTab): boolean => tab.index < tab.history.length - 1;

export function step(tab: BrowserTab, delta: number): BrowserTab {
  const index = Math.min(tab.history.length - 1, Math.max(0, tab.index + delta));
  const url = tab.history[index] ?? tab.url;
  return {
    ...tab,
    index,
    url,
    title: titleFor(url),
    status: internalPageOf(url) ? 'loaded' : 'loading',
  };
}

/** Newest first, one entry per URL — a history list that repeats itself is a log, not a history. */
export function addHistory(
  entries: readonly HistoryEntry[],
  entry: HistoryEntry,
  limit = 500,
): HistoryEntry[] {
  if (internalPageOf(entry.url)) return [...entries];
  return [entry, ...entries.filter((existing) => existing.url !== entry.url)].slice(0, limit);
}

export function toggleBookmark(marks: readonly Bookmark[], mark: Bookmark): Bookmark[] {
  const without = marks.filter((existing) => existing.url !== mark.url);
  return without.length === marks.length ? [mark, ...marks] : without;
}

export const isBookmarked = (marks: readonly Bookmark[], url: string): boolean =>
  marks.some((mark) => mark.url === url);
